import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPublicStats } from '../services/api';
import { COMPANY } from '../pages/legal/legalDocs';

const Footer = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    getPublicStats().then(r => setStats(r.data)).catch(() => {});
  }, []);

  const scrollOrGo = (path, scrollId) => {
    if (scrollId) {
      navigate('/');
      setTimeout(() => document.getElementById(scrollId)?.scrollIntoView({ behavior: 'smooth' }), 300);
    } else {
      navigate(path);
    }
  };

  return (
    <footer style={{ backgroundColor: '#e0e2e6' }} className="pt-14 pb-8 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">

          {/* Column 1 — Brand */}
          <div>
            <div className="text-2xl font-bold text-gray-900 mb-3">
              Oto<span className="text-blue-700">ParçamıBul</span>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              Oto yedek parça talep platformu. Araç sahiplerini güvenilir tedarikçilerle buluşturarak doğru parçayı uygun fiyata bulmanızı kolaylaştırıyoruz.
            </p>
            {stats && (
              <p className="text-gray-400 text-xs">
                {Number(stats.suppliers).toLocaleString('tr-TR')} tedarikçi · {stats.cities} il · {Number(stats.requests).toLocaleString('tr-TR')} talep
              </p>
            )}
          </div>

          {/* Column 2 — Menu */}
          <div>
            <h4 className="text-xs font-semibold text-gray-800 mb-4 uppercase tracking-wider">Menü</h4>
            <ul className="space-y-2.5">
              {[
                { label: 'Ana Sayfa',         path: '/'         },
                { label: 'Parça Talebi',      path: '/request'  },
                { label: 'Parça Stoku',       path: '/stock'    },
                { label: 'Kategoriler',       path: '/kategoriler' },
                { label: 'Tedarikçiler',      path: '/', scrollId: 'supplier-cta' },
                { label: 'Tedarikçi Girişi',  path: '/login'    },
                { label: 'Nasıl Çalışır?',    path: '/how-it-works' },
              ].map(l => (
                <li key={l.label}>
                  <button
                    onClick={() => scrollOrGo(l.path, l.scrollId)}
                    className="text-sm text-gray-600 hover:text-blue-700 transition"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3 — Legal */}
          <div>
            <h4 className="text-xs font-semibold text-gray-800 mb-4 uppercase tracking-wider">Yasal</h4>
            <ul className="space-y-2.5">
              {[
                { label: 'Kullanım Şartları',        slug: 'kullanim-sartlari' },
                { label: 'Tedarikçi Sözleşmesi',     slug: 'tedarikci-sozlesmesi' },
                { label: 'Gizlilik Politikası',      slug: 'gizlilik-politikasi' },
                { label: 'KVKK Aydınlatma Metni',    slug: 'kvkk-aydinlatma' },
                { label: 'Çerez Politikası',         slug: 'cerez-politikasi' },
                { label: 'Teslimat ve İade',         slug: 'teslimat-iade' },
                { label: 'İlan / Teklif Kuralları',  slug: 'kurallar' },
              ].map(l => (
                <li key={l.slug}>
                  <button onClick={() => navigate(`/yasal/${l.slug}`)}
                    className="text-sm text-gray-600 hover:text-blue-700 transition text-left">{l.label}</button>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4 — Contact */}
          <div>
            <h4 className="text-xs font-semibold text-gray-800 mb-4 uppercase tracking-wider">İletişim</h4>
            <ul className="space-y-3">
              <li>
                <a href={`mailto:${COMPANY.email}`} className="flex items-start gap-2.5 text-sm text-gray-600 hover:text-blue-700 transition">
                  <span className="mt-0.5">✉️</span> {COMPANY.email}
                </a>
              </li>
              <li>
                <a href={`tel:+9${COMPANY.phone.replace(/\s/g, '')}`} className="flex items-start gap-2.5 text-sm text-gray-600 hover:text-blue-700 transition">
                  <span className="mt-0.5">📞</span> {COMPANY.phone}
                </a>
              </li>
              <li className="flex items-start gap-2.5 text-sm text-gray-600">
                <span className="mt-0.5">📍</span> {COMPANY.address}
              </li>
            </ul>

            <div className="flex gap-3 mt-6">
              {['Twitter', 'Instagram', 'LinkedIn'].map(s => (
                <button key={s} className="text-xs text-gray-500 hover:text-blue-700 border border-gray-300 px-3 py-1.5 rounded-lg transition">
                  {s}
                </button>
              ))}
            </div>
          </div>

        </div>

        {/* Güvenli ödeme — iyzico (Visa & Mastercard) */}
        <div className="border-t border-gray-300 pt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
          <span className="text-xs text-gray-500 font-medium">iyzico ile Güvenli Ödeme · Visa · Mastercard</span>
          <img
            src={`${process.env.PUBLIC_URL || ''}/payment/iyzico-logos.svg`}
            alt="iyzico ile güvenli ödeme — Visa ve Mastercard"
            className="h-8 w-auto"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </div>

        {/* Bottom bar */}
        <div className="pt-5 mt-5 border-t border-gray-200 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-500">
            © {new Date().getFullYear()} OtoParçamıBul · {COMPANY.name} · {COMPANY.tax}. Tüm hakları saklıdır.
          </p>
          <p className="text-xs text-gray-400">
            Türkiye'nin Oto Yedek Parça Platformu
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;